import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface Project {
  _id: string;
  name: string;
  description: string;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  startDate: string;
  endDate: string;
  budget: number;
  actualCost?: number;
  progress: number;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  client: {
    name: string;
    contact: {
      email: string;
      phone: string;
    };
  };
  manager: string | { _id: string; name: string; email: string };
  team: Array<string | { _id: string; name: string; role: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectFilters {
  status?: string;
  manager?: string;
  client?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  totalBudget: number;
  totalActualCost: number;
  avgProgress: number;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class ProjectsService {
  // Get all projects with optional filters
  async getAll(filters: ProjectFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    return api.get(`/projects?${params.toString()}`);
  }

  // Get project by ID
  async getById(id: string) {
    return api.get(`/projects/${id}`);
  }

  // Create new project
  async create(projectData: Omit<Project, '_id' | 'createdAt' | 'updatedAt'>) {
    console.log('[projectsService] create() called', {
      url: `${API_BASE_URL}/projects`,
      keys: Object.keys(projectData || {}),
    });
    try {
      const response = await api.post('/projects', projectData);
      console.log('[projectsService] create() response', {
        status: response.status,
        ok: response.status >= 200 && response.status < 300,
        dataKeys: response?.data ? Object.keys(response.data) : [],
      });
      return response;
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      console.error('[projectsService] create() error', { status, data });
      throw error;
    }
  }

  // Update project
  async update(id: string, projectData: Partial<Project>) {
    return api.put(`/projects/${id}`, projectData);
  }

  // Delete project
  async delete(id: string) {
    return api.delete(`/projects/${id}`);
  }

  // Get project statistics
  async getStats(): Promise<{ data: ProjectStats }> {
    return api.get('/projects/stats');
  }

  // Get projects by status
  async getByStatus(status: string) {
    return api.get(`/projects/status/${status}`);
  }

  // Get projects by manager
  async getByManager(managerId: string) {
    return api.get(`/projects/manager/${managerId}`);
  }

  // Search projects
  async search(query: string, filters: ProjectFilters = {}) {
    const params = new URLSearchParams({ search: query });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    return api.get(`/projects/search?${params.toString()}`);
  }

  // Update project progress
  async updateProgress(id: string, progress: number) {
    return api.patch(`/projects/${id}/progress`, { progress });
  }

  // Update project status
  async updateStatus(id: string, status: string) {
    return api.patch(`/projects/${id}/status`, { status });
  }

  // Get project timeline
  async getTimeline(id: string) {
    return api.get(`/projects/${id}/timeline`);
  }

  // Get project budget summary
  async getBudgetSummary(id: string) {
    return api.get(`/projects/${id}/budget`);
  }

  // Export projects data
  async exportData(filters: ProjectFilters = {}, format = 'csv') {
    const params = new URLSearchParams({ format });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    
    const response = await api.get(`/projects/export?${params.toString()}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `projects_export.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Get recent projects
  async getRecent(limit = 10) {
    // Use existing projects endpoint with pagination and sorting
    return api.get('/projects', {
      params: {
        page: 1,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      },
    });
  }

  // Get project dashboard data
  async getDashboardData() {
    return api.get('/projects/dashboard');
  }
}

export const projectsService = new ProjectsService();
export default projectsService;